import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SeenLogo, SeenIcon } from '@/components/common/SeenLogo';
import { 
  Heart, 
  Eye, 
  Users, 
  ArrowRight,
  Quote,
  Calendar,
  AlertTriangle
} from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <SeenIcon size={32} />
              <span className="font-bold text-[#5DBDB3] text-lg tracking-wide">SEEN</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/">
                <Button variant="ghost">Home</Button>
              </Link>
              <Link to="/auth">
                <Button className="rounded-full px-6 bg-[#5DBDB3] hover:bg-[#4DA9A0]">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <SeenLogo size="lg" showTagline className="justify-center mx-auto mb-8" />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Our <span className="text-[#5DBDB3]">Story</span>
            </h1>
            <p className="text-xl text-muted-foreground">
              Why we're fighting for everyone with rare allergies to be seen
            </p>
          </div>
        </div>
      </section>

      {/* Andy's Story */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-5">
                {/* Image side */}
                <div className="md:col-span-2 bg-gradient-to-br from-[#5DBDB3] to-[#4DA9A0] p-8 flex flex-col justify-center items-center text-white">
                  <div className="w-32 h-32 rounded-full bg-white/20 flex items-center justify-center mb-6">
                    <Heart className="w-16 h-16 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Andy</h2>
                  <p className="text-white/80 text-center">Founder & CEO</p>
                  <div className="flex items-center gap-2 mt-4 text-white/70">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">40+ years allergy advocacy</span>
                  </div>
                </div>
                
                {/* Story side */}
                <div className="md:col-span-3 p-8 md:p-12">
                  <Quote className="w-10 h-10 text-[#5DBDB3]/30 mb-4" />
                  
                  <div className="space-y-6 text-lg leading-relaxed">
                    <p>
                      <span className="font-semibold text-[#5DBDB3]">I'm Andy</span>, Founder and CEO of Seen Beyond the Label. For over <strong>four decades</strong>, I've lived with multiple food allergies. While soya and peanuts are recognised as part of the 14 mandatory allergens, my other allergies — including legumes — are not.
                    </p>
                    
                    <p>
                      This means that despite affecting <strong className="text-[#F08B7D]">around 10% of the global population</strong>, these allergies remain <strong>completely invisible on product labels</strong>. No warnings. No ingredient highlights. No safety net.
                    </p>
                    
                    <div className="bg-[#F08B7D]/10 border border-[#F08B7D]/20 rounded-xl p-6 my-8">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-6 h-6 text-[#F08B7D] flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-[#F08B7D] mb-1">The Gap in Current Labelling</p>
                          <p className="text-muted-foreground">
                            Current regulations only mandate labelling for 14 allergens, yet over 170 foods can trigger allergic reactions. Millions of people are left without the information they need to stay safe.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <p>
                      This personal experience inspired me to create <strong className="text-[#5DBDB3]">Seen Beyond the Label</strong> — but our mission extends far beyond rare allergies. We're here to support <em>everyone</em> navigating food allergies, whether you're managing one of the common 14 or living with a condition that the current system overlooks entirely.
                    </p>
                    
                    <p className="text-muted-foreground border-l-4 border-[#5DBDB3] pl-4 italic">
                      Our mission is to deliver comprehensive ingredient transparency for all, ensuring that no one — regardless of how common or rare their allergy — has to navigate food choices without the information they deserve.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our <span className="text-[#F08B7D]">Mission</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Making the invisible visible, one ingredient at a time
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border border-border text-center p-8">
              <div className="w-16 h-16 rounded-2xl bg-[#5DBDB3]/10 flex items-center justify-center mx-auto mb-6">
                <Eye className="w-8 h-8 text-[#5DBDB3]" />
              </div>
              <h3 className="text-xl font-semibold mb-3">See Beyond</h3>
              <p className="text-muted-foreground">
                Uncover hidden ingredients that traditional labels don't show, including rare allergens.
              </p>
            </Card>
            
            <Card className="border border-border text-center p-8">
              <div className="w-16 h-16 rounded-2xl bg-[#F08B7D]/10 flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-[#F08B7D]" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Include Everyone</h3>
              <p className="text-muted-foreground">
                Support for ALL allergies, not just the main 14. Custom allergens are first-class citizens.
              </p>
            </Card>
            
            <Card className="border border-border text-center p-8">
              <div className="w-16 h-16 rounded-2xl bg-[#5DBDB3]/10 flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-[#5DBDB3]" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Protect Lives</h3>
              <p className="text-muted-foreground">
                From ingredient scanning to festival wristbands, keeping people safe is our purpose.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#5DBDB3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <p className="text-4xl md:text-5xl font-bold mb-2">14</p>
              <p className="text-white/80">Mandatory Allergens</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-bold mb-2">170+</p>
              <p className="text-white/80">Foods Cause Reactions</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-bold mb-2">10%</p>
              <p className="text-white/80">Affected Globally</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-bold mb-2">100%</p>
              <p className="text-white/80">Deserve Protection</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Join Us in Making Allergies <span className="text-[#5DBDB3]">Visible</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Whether you have a common allergy or a rare one that affects only 1% of the population, 
            you deserve to know what's in your food.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/auth">
              <Button size="lg" className="rounded-full px-8 bg-[#5DBDB3] hover:bg-[#4DA9A0]">
                Start Scanning Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/subscription">
              <Button size="lg" variant="outline" className="rounded-full px-8 border-[#5DBDB3] text-[#5DBDB3]">
                View Business Plans
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <SeenIcon size={28} />
              <span className="font-bold text-[#5DBDB3]">SEEN</span>
              <span className="text-muted-foreground text-sm">Beyond the label.</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 Seen Beyond the Label. Making rare allergies visible.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
